import { rem } from 'polished'
import React, { useCallback, useContext } from 'react'
import styled from 'styled-components'

import BookedLabel from './BookedLabel'
import ItemMenuOptions from './ItemMenuOptions'
import FlightItemCardFlightName from '../Flight/FlightItemCardFlightName'

import Group from 'components/utils/Group'
import { AddRemoveItineraryButton } from 'tripPlanner/components/Itinerary/AddRemoveItineraryButton'
import ItineraryDateButton from 'tripPlanner/components/Itinerary/ItineraryDateButton'
import OutsideTripDatesToolTip from 'tripPlanner/components/Itinerary/OutsideTripDatesToolTip'
import EditTripItem from 'tripPlanner/components/TripModal/TripItems/Edit'
import AddedUpdatedBy from 'tripPlanner/components/TripModal/TripItems/Summary/Common/AddedUpdatedBy'
import EditTripDates from 'tripPlanner/components/TripModal/Trips/EditTripDatesTP'
import TripContext from 'tripPlanner/contexts/TripContext'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import ItemTileMenuButton from 'tripPlanner/Tripkit/Core/MenuButton/MenuButton'
import { FlightItem } from 'tripPlanner/types/tripItem'
import { isCustomItem, typeConstToTitle } from 'tripPlanner/utils'

const MenuButton = styled(ItemTileMenuButton)`
  margin-right: ${rem(-4)};

  @media print {
    display: none;
  }
`

const PaddedBox = styled.div`
  padding: ${rem(12)};
`

const StyledTopContent = styled.div`
  width: 100%;
`

const FlightName = styled(FlightItemCardFlightName)`
  flex-grow: 1;
`

interface Props {
  item: FlightItem
}

function FlightTopContent({ item }: Props) {
  const { tripId, createdAt, createdBy, userUpdatedAt, updatedBy } = item
  const tripContext = useContext(TripContext)
  const canEdit = tripContext?.permissions.canEdit ?? false
  const openModal = useOpenModal()

  const onClickAddTripItemDates = useCallback(
    () => openModal(EditTripItem, { item }),
    [item, openModal],
  )

  const onRequestAddTripDates = useCallback(
    (
      onDatesAdded?: (startDate: moment.Moment, endDate: moment.Moment) => void,
    ) => {
      openModal(EditTripDates, { tripId, onDatesAdded })
    },
    [openModal, tripId],
  )

  return (
    <StyledTopContent>
      <Group
        direction="vertical-reverse"
        tabletDirection="horizontal"
        horizontalAlign="space-between"
        verticalAlign="center"
        gap={8}
      >
        <FlightName item={item} />
        <Group
          direction="horizontal"
          verticalAlign="center"
          gap={8}
          horizontalAlign="end"
        >
          <OutsideTripDatesToolTip item={item} />
          {'isBooked' in item && item.isBooked && <BookedLabel />}
          {canEdit && (
            <Group direction="horizontal" gap={8} verticalAlign="center">
              {isCustomItem(item) && (
                <ItineraryDateButton
                  item={item}
                  variant="chip"
                  onRequestAddTripDates={onRequestAddTripDates}
                />
              )}
              {!isCustomItem(item) && (
                <AddRemoveItineraryButton
                  item={item}
                  onClickAddTripItemDates={onClickAddTripItemDates}
                />
              )}
              <MenuButton
                title={typeConstToTitle(item.type)}
                kind="secondary"
                variant="dark"
                shape="circle"
                bodyContent={<ItemMenuOptions item={item} />}
                footerContent={
                  createdAt &&
                  createdBy &&
                  updatedBy &&
                  userUpdatedAt && (
                    <PaddedBox>
                      {/* THIS IS A HACK
                    these fields will not exist on public trip items
                    this is a temporary hack to ensure public trip can utilise the
                    viewer trip context values for stable application behaviour
                  */}
                      <AddedUpdatedBy
                        tripId={tripId}
                        createdAt={createdAt}
                        createdBy={createdBy}
                        updatedAt={userUpdatedAt}
                        updatedBy={updatedBy}
                        avatarSize="small"
                      />
                    </PaddedBox>
                  )
                }
              />
            </Group>
          )}
        </Group>
      </Group>
    </StyledTopContent>
  )
}

export default FlightTopContent
